<template>
  <OmnisearchEventsField v-if="kind === 'events'" v-bind="eventsBindings" @field-mounted="handleEventsFieldMounted" />
  <OmnisearchProviderField
    v-else
    v-bind="providerBindings"
    @select="handleSelect"
    @clear="handleClear"
    @text-change="handleTextChange"
    @submit="handleSubmit"
  />
</template>

<script setup>
/**
 * One search box for several finder domains. `kind` picks the field: `provider` reports to the
 * doctor-finder wizard through `select`, `clear`, `text-change` and `submit` events, and `events`
 * submits through the host's form. The host is form-associated for the events field. The provider
 * field sets no form value, so a provider mount adds nothing to its form.
 *
 * @displayName FinderOmnisearch
 * @group Custom Elements
 * @component sp-finder-omnisearch
 */
import { computed, defineAsyncComponent } from "vue";
import { useHostElement } from "#ce/composables/useHostElement";
import { holdMountReady } from "#ce/framework/mount-ready";
import { formAssociatedHost } from "#ce/framework/utils";
import OmnisearchProviderField from "#ce/apps/SpFinderOmnisearch/components/OmnisearchProviderField.ce.vue";
import { useOmnisearchKind } from "#ce/apps/SpFinderOmnisearch/composables/useOmnisearchKind";
import { EVENTS_PROP_NAMES, PROVIDER_PROP_NAMES, props as omnisearchProps } from "./props.js";

function createDeferred() {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });

  return { promise, resolve, reject };
}

function loadEventsField() {
  return import("#ce/apps/SpFinderOmnisearch/components/OmnisearchEventsField.ce.vue");
}

defineOptions(formAssociatedHost());
const props = defineProps(omnisearchProps);
const emit = defineEmits(["select", "clear", "text-change", "submit"]);

const kind = useOmnisearchKind(props.kind);

// An async child does not delay the host's ready marker, so the events host waits until its field
// has mounted, or until the field's chunk fails to load.
const eventsFieldMounted = kind === "events" ? createDeferred() : null;

function handleEventsFieldMounted() {
  eventsFieldMounted?.resolve();
}

function handleEventsFieldLoadError(error, retry, fail) {
  eventsFieldMounted?.reject(error);
  fail();
}

if (eventsFieldMounted) {
  holdMountReady(useHostElement(), eventsFieldMounted.promise);
}

// Loaded on demand, so a provider mount does not download the events field. A failed load renders
// nothing; the host is marked failed through the hold above.
const OmnisearchEventsField = defineAsyncComponent({
  loader: loadEventsField,
  errorComponent: { render: () => null },
  onError: handleEventsFieldLoadError,
});

function pick(names) {
  const picked = {};

  for (const name of names) {
    if (props[name] !== undefined) {
      picked[name] = props[name];
    }
  }

  return picked;
}

const providerBindings = computed(() => pick(PROVIDER_PROP_NAMES));
const eventsBindings = computed(() => pick(EVENTS_PROP_NAMES));

function handleSelect(detail) {
  emit("select", detail);
}

function handleClear() {
  emit("clear");
}

function handleTextChange(text) {
  emit("text-change", text);
}

function handleSubmit() {
  emit("submit");
}
</script>

<style>
:host {
  display: block;
  width: 100%;
}

:host([hidden]) {
  display: none;
}
</style>
